import { type PersistStorage, persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";
import { toast } from "@/components/ui/sonner";
import type { CartUpdateAction } from "@/features/cart/types";
import type { Cart } from "@/features/cart/types/cart.type";
import type { OrderInvoice } from "@/features/cart/types/invoice-data.type";
import type { ContactInfoReturnType } from "@/features/checkout/forms";
import type {
  DietCatalog,
  DietCatalogCateringType,
} from "@/features/diets/types";
import {
  getObfuscatedLocalStorage,
  setObfuscatedLocalStorage,
} from "@/lib/storage";

type ExtendDietCartState = Cart & {
  // cartId is taken from the API - it's a unique identifier for the cart
  cartId?: string;

  // if the user created an order, we should store it for updating the cart
  currentOrderId?: string;

  // invoices
  invoiceData: OrderInvoice | null;
  invoiceActive: boolean;
  invoiceUseSameAddress?: boolean;

  // address error
  addressNotSupported?: boolean | null;

  // delivery costs
  deliveryCosts?: {
    itemId: string;
    dietId: string;
    cost: DietCatalogCateringType["deliveryCosts"];
  }[];

  // order processing state
  isProcessingOrder: boolean;

  // global loading state
  isLoading: boolean;
};

type ExtendDietCartActions = {
  setCartId: (cartId: string) => void;
  setCart: (cart: Cart) => void;
  resetCart: () => void;

  updateCart: (
    cartId: string,
    cart: Cart,
    action: CartUpdateAction,
    t?: (key: string) => string,
  ) => Cart;

  getDietIds: () => string;

  setOrderId: (orderId: string) => void;
  getAddress: () => Partial<ContactInfoReturnType> | undefined;
  getInvoiceData: () => OrderInvoice | null;
  setInvoiceData: (invoiceData: OrderInvoice) => void;
  setInvoiceUseSameAddress: (useSameAddress: boolean) => void;

  setAddressNotSupported: (value: boolean) => void;

  addDeliveryCost: (
    itemId: string,
    dietId: string,
    cost: DietCatalogCateringType["deliveryCosts"],
  ) => void;

  removeDeliveryCost: (itemId: string) => void;
  clearDeliveryCost: () => void;
  getDeliveryCost: () => DietCatalogCateringType["deliveryCosts"] | null;
  setDeliveryCosts: (diets: DietCatalog[]) => void;

  setIsProcessingOrder: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
};

type ExtendDietCartStore = ExtendDietCartState & ExtendDietCartActions;

const defaultValues: ExtendDietCartState = {
  cartId: undefined,
  currentOrderId: undefined,
  addressNotSupported: null,
  items: [],
  pricing: {
    final: {
      gross: 0,
      net: 0,
      tax: 0,
    },
    before: {
      gross: 0,
      net: 0,
      tax: 0,
    },
    discounts: [],
    delivery: {
      gross: 0,
      net: 0,
      tax: 0,
    },
  },
  discountCode: null,
  invoiceData: null,
  invoiceActive: false,
  agreements: {
    cateringPolicy: false,
    terms: false,
    marketing: false,
    privacy: false,
  },
  isProcessingOrder: false,
  isLoading: false,
};

const storage: PersistStorage<ExtendDietCartState> = {
  getItem: (name: string) => {
    return getObfuscatedLocalStorage(name);
  },
  setItem: (name: string, value: unknown) => {
    setObfuscatedLocalStorage(name, value);
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

const createExtendDietCartStore = ({ storageKey }: { storageKey: string }) =>
  createStore<ExtendDietCartStore>()(
    persist(
      (set, get) => ({
        ...defaultValues,

        setCartId: (cartId: string) => set({ cartId }),
        setCart: (cart: Cart) => {
          return set((state) => {
            if (!cart.pricing) {
              return cart;
            }
            if (!cart.pricing.delivery && state.pricing?.delivery) {
              return {
                ...cart,
                pricing: {
                  ...cart.pricing,
                  delivery: state.pricing.delivery,
                },
              };
            }
            return cart;
          });
        },
        resetCart: () => set(defaultValues),

        updateCart: (cartId, cart, action, t) => {
          get().setCartId(cartId);
          set((state) => {
            if (!cart.pricing) {
              return cart;
            }
            if (!cart.pricing.delivery && state.pricing?.delivery) {
              return {
                ...cart,
                pricing: {
                  ...cart.pricing,
                  delivery: state.pricing.delivery,
                },
              };
            }
            return cart;
          });

          const translate = (message: string) => (t ? t(message) : message);

          switch (action) {
            case "add":
              toast.success(translate("cart.added"));
              break;
            case "remove":
              toast.info(translate("cart.removed"));
              break;
            case "clear":
              toast.info(translate("cart.cleared"));
              break;
            case "update":
              toast.success(translate("cart.updated"));
              break;
            case "addDiscount":
              toast.success(translate("cart.addedDiscount"));
              break;
            case "addWrongDiscount":
              toast.error(translate("cart.addedWrongDiscount"));
              break;
            case "removeDiscount":
              toast.info(translate("cart.removedDiscount"));
              break;
            default:
              break;
          }

          return get();
        },

        getDietIds: () => {
          return get()
            .items.map((item) => item.configurationItem.dietId)
            .join(",");
        },

        setOrderId: (orderId: string) => set({ currentOrderId: orderId }),

        getAddress: () => {
          const items = get().items;
          if (items.length > 0) {
            const item = items[0]?.configurationItem;
            if (item) {
              const contactStore: Partial<ContactInfoReturnType> = {
                address: item.address,
                email: item.contact?.email,
                phone: item.contact?.phoneNumber,
                firstName: item.contact?.firstName,
                lastName: item.contact?.lastName,
              };
              return contactStore;
            }
          }
        },

        getInvoiceData: () => {
          if (get().invoiceActive && get().invoiceData) {
            const data = get().invoiceData!;
            return {
              active: true,
              companyName: data.companyName,
              vatNumber: data.vatNumber,
              postCode: data.postCode,
              city: data.city,
              street: data.street,
              buildNumber: data.buildNumber,
              flatNumber: data.flatNumber,
            };
          }
          return null;
        },

        setInvoiceData: (invoiceData: OrderInvoice | null) => {
          set({ invoiceData });
        },

        setInvoiceUseSameAddress: (useSameAddress: boolean) =>
          set({ invoiceUseSameAddress: useSameAddress }),

        setAddressNotSupported: (value: boolean) =>
          set({ addressNotSupported: value }),

        addDeliveryCost: (itemId, dietId, cost) => {
          set((state) => ({
            deliveryCosts: [
              ...(state.deliveryCosts || []),
              { itemId, dietId, cost },
            ],
          }));
        },

        removeDeliveryCost: (itemId) => {
          set((state) => ({
            deliveryCosts: (state.deliveryCosts || []).filter(
              (item) => item.itemId !== itemId,
            ),
          }));
        },

        clearDeliveryCost: () => {
          set({ deliveryCosts: [] });
        },

        getDeliveryCost: () => {
          if (get().items.length === 0) {
            return null;
          }
          const costs = get().deliveryCosts?.map((cost) => cost.cost);
          if (!costs) {
            return null;
          }

          if (
            costs.every(
              (val) => JSON.stringify(val) === JSON.stringify(costs[0]),
            ) &&
            costs[0]
          ) {
            return costs[0];
          }

          return null;
        },

        setDeliveryCosts: (diets: DietCatalog[]) => {
          const deliveryCosts = get().deliveryCosts || [];
          const items = get().items;

          if (!items.length) {
            return;
          }

          const missingItems = items.filter(
            (item) =>
              !deliveryCosts.some(
                (cost) => cost.itemId === item.configurationItem.id,
              ),
          );

          if (!missingItems.length) {
            return;
          }

          const missingCosts = missingItems.map((item) => {
            const diet = diets.find(
              (d) => d.id === item.configurationItem.dietId,
            );

            if (!(diet && item.configurationItem.id)) {
              return null;
            }

            return {
              itemId: item.configurationItem.id,
              dietId: diet.id,
              cost: diet.catering.deliveryCosts,
            };
          });

          for (const diet of missingCosts) {
            if (!diet) {
              return;
            }
            get().addDeliveryCost(diet.itemId, diet.dietId, diet.cost);
          }
        },

        setIsProcessingOrder: (value: boolean) =>
          set({ isProcessingOrder: value }),

        setIsLoading: (value: boolean) => set({ isLoading: value }),
      }),
      {
        name: storageKey,
        storage,
      },
    ),
  );

export { createExtendDietCartStore, type ExtendDietCartStore };
